import {z} from "zod/v4-mini"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {MeshNodeDefinition, MeshNodeParameters} from "#template/nodes/object/mesh/types"
import {MeshNode, maxSubdivisionLimit} from "#template/nodes/object/mesh/mesh-node"
import {meshNodeParameters} from "#template/nodes/object/mesh/validation"
import {booleanLikeNode} from "#template/nodes/boolean/fundamental"
import {jsonLikeNode} from "#template/nodes/json/fundamental"
import {numberLikeNode} from "#template/nodes/number/fundamental"
import {stringLike, stringLikeNode} from "#template/nodes/string/fundamental"
import type {Evaluator, Json} from "@cm/graph-v3"
import {jsonSchema} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import type {BooleanLikeNode, JsonLikeNode, NumberLikeNode, StringLike, StringLikeNode} from "#template/nodes/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import type {AnyJsonValue} from "#template/value-types"
import type {AbstractMeshData} from "#template/interfaces/mesh-data"
import {GenerateMesh} from "#template/runtime-graph/nodes/generate-mesh"
import {commonGeometryGraphTable} from "#template/geometry-processing/common-geometry-graphs"
import {geomToMesh} from "#template/geometry-processing/geometry-graph"
import type {MeshNodes} from "@cm/render-nodes"
import {hashObject, mapFields} from "@cm/utils"
import {isEvaluatedTemplateNode} from "#template/utils/template-node-utils"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {visitNone} from "#template/template-node/visit-helpers"

type GeometryParameters = Record<string, AnyJsonValue>

type EvaluableGeometryParameter = AnyJsonValue | NumberLikeNode | StringLikeNode | BooleanLikeNode | JsonLikeNode | undefined

function getGeometryGraph(geometryGraph: string, parameters: GeometryParameters): MeshNodes.Mesh | null {
    const fn = commonGeometryGraphTable[geometryGraph]
    return fn ? geomToMesh(fn(parameters ?? {})) : null
}

function evaluateGeometryParameter(evaluableGeometryParameter: EvaluableGeometryParameter, _scope: GraphBuilderScope, evaluator: NodeEvaluator) {
    return evaluator.evaluate(evaluableGeometryParameter)
}

export type ProceduralMeshParameters = {
    name: string
    parameters: Record<string, Json | StringLikeNode | NumberLikeNode | BooleanLikeNode | JsonLikeNode | undefined>
    geometryGraph: StringLike
} & MeshNodeParameters

const proceduralMeshDefinition: MeshNodeDefinition<ProceduralMeshParameters> = {
    nodeClass: "ProceduralMesh",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                parameters: z.record(z.string(), z.optional(z.union([jsonSchema, stringLikeNode, numberLikeNode, booleanLikeNode, jsonLikeNode]))),
                geometryGraph: stringLike,
            }),
            meshNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ProceduralMesh extends MeshNode<ProceduralMeshParameters> {
    public override get definition() {
        return proceduralMeshDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ProceduralMeshParameters> {
        const {evaluator} = context
        const {templateContext, activeNodeSet} = evaluator
        const {sceneManager, computeDummyReifiedMeshData} = templateContext
        const {geometryGraph: evaluableGeometryGraphName, parameters: evaluableGeometryParameters, subdivisionRenderIterations} = this.parameters

        const scope = evaluator.getScope(this)

        const evaluatedParams = mapFields(evaluableGeometryParameters, (value) => evaluateGeometryParameter(value, scope, evaluator))
        const filteredParams: Record<string, AnyJsonValue> = {}
        for (const key in evaluatedParams) {
            const v = evaluatedParams[key]
            if (v !== undefined) filteredParams[key] = v as AnyJsonValue
        }
        const geometryParameters = scope.record<string, AnyJsonValue>(filteredParams)

        const [geometryGraphNameValid, geometryGraphNameInvalid] = scope.branch(evaluator.evaluate(evaluableGeometryGraphName))

        const [graphValid, graphInvalid] = scope.branch(
            scope.pureLambda<[string, GeometryParameters], MeshNodes.Mesh | null>(
                scope.tuple(geometryGraphNameValid, geometryParameters),
                ([geometryGraphName, geometryParameters]) => getGeometryGraph(geometryGraphName, geometryParameters),
                "geometryGraph",
            ),
        )

        const contentHash = scope.pureLambda<[string, GeometryParameters, number | undefined], string>(
            scope.tuple(geometryGraphNameValid, geometryParameters, subdivisionRenderIterations),
            ([geometryGraphName, geometryParameters, subdivisionRenderIterations]) =>
                hashObject({geometryGraphName, geometryParameters, subdivisionRenderIterations}),
            "contentHash",
        )

        const displaySubdivLevel = this.getDisplaySubdivisionLevel(context)

        const inputMeshData = scope.pureLambda<[string, MeshNodes.Mesh, number, number | undefined, number], AbstractMeshData>(
            scope.tuple(contentHash, graphValid, displaySubdivLevel, subdivisionRenderIterations, maxSubdivisionLimit),
            ([contentHash, graph, displaySubdivLevel, subdivisionRenderIterations, maxSubdivisionLimit]) => {
                let displayGeometryGraph = graph
                let renderGeometryGraph = graph

                if (displaySubdivLevel > 0) {
                    displayGeometryGraph = {type: "subdivide", input: displayGeometryGraph, levels: displaySubdivLevel}
                }

                const renderSubdivLevel = Math.min(subdivisionRenderIterations ?? 0, maxSubdivisionLimit)
                if (renderSubdivLevel > 0) {
                    renderGeometryGraph = {type: "subdivide", input: renderGeometryGraph, levels: renderSubdivLevel}
                }

                return {
                    contentHash,
                    displayGeometryGraph,
                    renderGeometryGraph,
                    displayGeometryGraphResources: {},
                }
            },
            "inputMeshData",
        )

        const {completeMeshData} = scope.node(GenerateMesh, {
            sceneManager,
            computeDummyReifiedMeshData: computeDummyReifiedMeshData && !isEvaluatedTemplateNode(this, activeNodeSet),
            inputMeshData,
        })

        this.setupMesh(scope, context, completeMeshData, true, scope.phi(graphInvalid, geometryGraphNameInvalid))

        return visitNone(this.parameters)
    }
}
